import React from "react";

/**
 * Projects sourced from the homepage "Featured Projects" on peiarchitect.com.
 * Imagery downloaded locally to /public/images/.
 */
const PROJECTS = [
  {
    name: "Beach Point Guesthouse",
    category: "Single Family Residential",
    descriptor:
      "A coastal guesthouse framed to the Northumberland Strait — quiet cedar lines against the dune grass.",
    img: `${process.env.PUBLIC_URL}/images/beach-point.jpg`,
    // bento placement
    d: "hero",
  },
  {
    name: "Spry Point Condos",
    category: "Multi-Family Residential",
    descriptor: "Seven residences set low against the shoreline.",
    img: `${process.env.PUBLIC_URL}/images/spry-point.webp`,
    d: "tall",
  },
  {
    name: "Fox River House",
    category: "Single Family Residential",
    descriptor: "A long plan oriented to the evening tide.",
    img: `${process.env.PUBLIC_URL}/images/fox-river.png`,
    d: "wide",
  },
  {
    name: "Stanhope Cottage",
    category: "Single Family Residential",
    descriptor: "A small cottage built around a warm kitchen.",
    img: `${process.env.PUBLIC_URL}/images/stanhope-cottage.jpg`,
    d: "square",
  },
  {
    name: "Gladstone House",
    category: "Single Family Residential",
    descriptor: "A quiet architecture of long lines and deep eaves.",
    img: `${process.env.PUBLIC_URL}/images/gladstone.jpg`,
    d: "med-a",
  },
  {
    name: "Spring Park Duplex",
    category: "Multi-Family Residential",
    descriptor: "Two homes woven into one pitched silhouette.",
    img: `${process.env.PUBLIC_URL}/images/spring-park.png`,
    d: "med-b",
  },
];

/* Bento placements keyed by `d` — desktop (md+) uses a 12-col × 4-row grid */
const DESKTOP_AREAS = {
  hero: "1 / 1 / 3 / 8",   // rows 1-2, cols 1-7
  tall: "1 / 8 / 3 / 13",  // rows 1-2, cols 8-12
  wide: "3 / 1 / 4 / 9",   // row 3,    cols 1-8
  square: "3 / 9 / 4 / 13",// row 3,    cols 9-12
  "med-a": "4 / 1 / 5 / 7",// row 4,    cols 1-6
  "med-b": "4 / 7 / 5 / 13",// row 4,   cols 7-12
};

/* Tablet (md only, <lg) uses a 6-col layout. lg uses 12-col (above). */
const TABLET_AREAS = {
  hero: "1 / 1 / 3 / 5",   // rows 1-2, cols 1-4
  tall: "1 / 5 / 3 / 7",   // rows 1-2, cols 5-6
  wide: "3 / 1 / 4 / 5",   // row 3,    cols 1-4
  square: "3 / 5 / 4 / 7", // row 3,    cols 5-6
  "med-a": "4 / 1 / 5 / 4",// row 4,    cols 1-3
  "med-b": "4 / 4 / 5 / 7",// row 4,    cols 4-6
};

/* Mobile aspect ratios alternate to keep rhythm */
const MOBILE_ASPECT = {
  hero: "4 / 3",
  tall: "3 / 4",
  wide: "16 / 10",
  square: "1 / 1",
  "med-a": "4 / 3",
  "med-b": "3 / 4",
};

const Card = ({ p, index }) => {
  const onClick = (e) => {
    e.preventDefault();
    document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
  };

  const isLarge = p.d === "hero" || p.d === "tall";

  return (
    <a
      href="#contact"
      onClick={onClick}
      data-testid={`project-card-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
      className="sb-bento-card group relative block overflow-hidden reveal"
      style={{
        // mobile fallback aspect ratio; on md+ the grid-row span locks the cell height
        aspectRatio: MOBILE_ASPECT[p.d],
        transitionDelay: `${(index % 3) * 70}ms`,
        "--bento-tablet": TABLET_AREAS[p.d],
        "--bento-desktop": DESKTOP_AREAS[p.d],
      }}
    >
      <div className="absolute inset-0 overflow-hidden">
        <img
          src={p.img}
          alt={p.name}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-[1.04]"
          loading="lazy"
          decoding="async"
        />
      </div>

      {/* Teal hover overlay */}
      <div
        className="absolute inset-0 flex items-end p-6 md:p-8 lg:p-10 transition-opacity duration-500 opacity-0 group-hover:opacity-100"
        style={{ backgroundColor: "rgba(58,125,123,0.86)" }}
      >
        <p
          className="font-display italic leading-[1.22] max-w-md"
          style={{
            color: "#f4f1eb",
            fontSize: isLarge ? "clamp(1.25rem, 1.9vw, 1.75rem)" : "clamp(1rem, 1.5vw, 1.35rem)",
          }}
        >
          {p.descriptor}
        </p>
      </div>

      {/* Bottom caption */}
      <div
        className="absolute inset-x-0 bottom-0 px-6 md:px-8 pb-6 md:pb-8 pt-16 group-hover:opacity-0 transition-opacity duration-500"
        style={{
          background:
            "linear-gradient(180deg, rgba(26,35,48,0) 0%, rgba(26,35,48,0.86) 100%)",
        }}
      >
        <h3
          className="font-display leading-[1.05] tracking-[-0.01em]"
          style={{
            color: "#f4f1eb",
            fontSize: isLarge
              ? "clamp(1.6rem, 2.6vw, 2.4rem)"
              : "clamp(1.3rem, 2vw, 1.85rem)",
            fontWeight: 400,
          }}
        >
          {p.name}
        </h3>
        <div
          className="mt-3 mb-3 h-[1px] w-10 transition-all duration-500 group-hover:w-16"
          style={{ backgroundColor: "#3a7d7b" }}
        />
        <p
          className="font-body text-[10px] md:text-[11px] uppercase"
          style={{ color: "rgba(244,241,235,0.78)", letterSpacing: "0.28em" }}
        >
          {p.category}
        </p>
      </div>
    </a>
  );
};

export const FeaturedProjects = () => {
  const viewAll = (e) => {
    e.preventDefault();
    document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="projects"
      data-testid="projects-section"
      className="py-28 md:py-40 relative"
    >
      <div
        aria-hidden
        className="absolute left-0 right-0 top-0 h-[1px]"
        style={{ backgroundColor: "rgba(200,184,154,0.22)" }}
      />
      <div className="container-editorial">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 mb-16 md:mb-24 items-end">
          <div className="md:col-span-5 reveal">
            <p
              className="text-[11px] uppercase font-body mb-6"
              style={{ color: "#3a7d7b", letterSpacing: "0.36em" }}
            >
              — Selected Work
            </p>
            <h2
              className="font-display leading-[1.02] tracking-[-0.018em]"
              style={{ fontSize: "clamp(2.25rem, 5vw, 4.75rem)", fontWeight: 400 }}
            >
              A practice measured in <em className="italic">decades</em>,
              not deadlines.
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 reveal">
            <p
              className="font-body text-base md:text-[17px] leading-[1.85] max-w-lg"
              style={{ color: "hsl(var(--foreground) / 0.8)" }}
            >
              Each project begins where the coastline does — with the wind, the grain of
              the soil, the angle of the afternoon sun. A selection of homes and buildings
              completed across Prince Edward Island.
            </p>
            <a
              href="https://www.peiarchitect.com/portfolio"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="projects-view-all"
              className="mt-8 inline-flex items-center gap-3 font-body text-[12px] tracking-[0.22em] uppercase pb-2 border-b transition-all hover:gap-5"
              style={{ color: "#3a7d7b", borderColor: "#3a7d7b" }}
            >
              View all projects
              <span aria-hidden>→</span>
            </a>
          </div>
        </div>

        {/* Bento grid */}
        <div className="sb-bento" data-testid="projects-bento">
          {PROJECTS.map((p, i) => (
            <Card key={p.name} p={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturedProjects;
